export const mdmLevels = [
  {
    key: 'minimal',
    label: 'Minimal',
    title: 'Straightforward',
    description: 'Low-complexity evaluation and management.',
    tip: 'Use only when the documented medical decision making is straightforward and the selected patient category supports the code.',
  },
  {
    key: 'low',
    label: 'Low',
    title: 'Low',
    description: 'Low-complexity medical decision making.',
    tip: 'Confirm that the selected problems, data, and risk are supported by the encounter documentation.',
  },
  {
    key: 'moderate',
    label: 'Moderate',
    title: 'Moderate',
    description: 'Moderate-complexity medical decision making.',
    tip: 'Prescription drug management is a common example of moderate management risk when documented.',
  },
  {
    key: 'high',
    label: 'High',
    title: 'High',
    description: 'High-complexity medical decision making.',
    tip: 'High-level services require substantial documentation supporting the severity, risk, or complexity involved.',
  },
];

export const mdmElements = [
  {
    key: 'problems',
    title: 'Problems Addressed',
    description: 'Number and complexity of problems addressed',
    options: [
      {
        level: 'minimal',
        text: '1 minor or self-limited problem',
      },
      {
        level: 'low',
        text: '2+ minor problems, 1 stable chronic illness, or 1 acute uncomplicated illness',
      },
      {
        level: 'moderate',
        text: '1+ chronic illness with exacerbation, 2+ stable chronic illnesses, or 1 undiagnosed problem with uncertain prognosis',
      },
      {
        level: 'high',
        text: 'Severe exacerbation or immediate threat to life or bodily function',
      },
    ],
  },
  {
    key: 'data',
    title: 'Data Reviewed',
    description: 'Amount and complexity of data reviewed and analyzed',
    options: [
      {
        level: 'minimal',
        text: 'Minimal or no data reviewed',
      },
      {
        level: 'low',
        text: 'Meet 1 category: external notes, unique tests ordered, or unique test results assessed',
      },
      {
        level: 'moderate',
        text: 'Meet 1 category: multiple tests, external discussion, or independent test interpretation',
      },
      {
        level: 'high',
        text: 'Meet 2+ categories: independent visualization, external discussion, and extensive record review',
      },
    ],
  },
  {
    key: 'risk',
    title: 'Management Risk',
    description: 'Risk of complications, morbidity, or mortality',
    options: [
      {
        level: 'minimal',
        text: 'Minimal risk, such as rest, gargling, or dressing change',
      },
      {
        level: 'low',
        text: 'OTC drugs, physical therapy, or minor surgery without risk factors',
      },
      {
        level: 'moderate',
        text: 'Prescription drug management, minor surgery with risk factors, or social determinants limiting care',
      },
      {
        level: 'high',
        text: 'Intensive toxicity monitoring, elective major surgery, or emergency surgery or hospitalization',
      },
    ],
  },
];

export const timeRanges = {
  new: [
    {
      code: '99202',
      range: '15–29 minutes',
      min: 15,
      max: 29,
    },
    {
      code: '99203',
      range: '30–44 minutes',
      min: 30,
      max: 44,
    },
    {
      code: '99204',
      range: '45–59 minutes',
      min: 45,
      max: 59,
    },
    {
      code: '99205',
      range: '60 minutes or more',
      min: 60,
      max: Infinity,
    },
  ],
  established: [
    {
      code: '99211',
      range: '0–9 minutes',
      min: 0,
      max: 9,
    },
    {
      code: '99212',
      range: '10–19 minutes',
      min: 10,
      max: 19,
    },
    {
      code: '99213',
      range: '20–29 minutes',
      min: 20,
      max: 29,
    },
    {
      code: '99214',
      range: '30–39 minutes',
      min: 30,
      max: 39,
    },
    {
      code: '99215',
      range: '40 minutes or more',
      min: 40,
      max: Infinity,
    },
  ],
};

export const getMdmCode = (patientType, levelKey) => {
  const newCodes = {
    minimal: '99202',
    low: '99203',
    moderate: '99204',
    high: '99205',
  };

  const establishedCodes = {
    minimal: '99211',
    low: '99212',
    moderate: '99214',
    high: '99215',
  };

  return patientType === 'new'
    ? newCodes[levelKey]
    : establishedCodes[levelKey];
};

export const getTimeResult = (patientType, minutes) => {
  const numericMinutes = Number(minutes);

  if (!Number.isFinite(numericMinutes) || numericMinutes < 0) {
    return null;
  }

  return timeRanges[patientType].find(
    (item) => numericMinutes >= item.min && numericMinutes <= item.max,
  ) || null;
};